import {
  ApproverType,
  ContentAudience,
  ContentWorkflow,
  PublisherReviewStatus,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { extractUserAlias } from '../../api/UtilsApi';
import { workflowStateDisplayNames, workflowStateToVisibilityMap } from '../utils/DisplayNames';
import { useWorkflow } from '../v2-hooks/useWorkflow';

export type AudienceLabel = 'Internal' | 'External';
export type VisibilityLabel = 'Hidden' | 'Internal' | 'External';

export interface PublicationMetadataViewModel {
  /** Display label for the current draft stage. */
  readonly stageLabel: string;
  /** Display label for the target audience. */
  readonly audienceLabel: AudienceLabel;
  /** Display label for the current visibility. */
  readonly visibilityLabel: VisibilityLabel;
  /** Alias of the deciding bar raiser, or undefined when not yet decided. */
  readonly barRaiserAlias: string | undefined;
  /** Display label for the bar raiser review status, or undefined when no review info exists. */
  readonly barRaiserReviewLabel: string | undefined;
}

const draftStates = new Set([WorkflowState.NEW, WorkflowState.UNMODIFIED, WorkflowState.MODIFIED]);

function getStageLabel(draftState: WorkflowState | undefined): string {
  if (!draftState) return 'Draft';
  if (draftStates.has(draftState)) return 'Draft';
  return workflowStateDisplayNames.get(draftState) ?? draftState;
}

function getAudienceLabel(audience: ContentAudience | undefined): AudienceLabel {
  switch (audience) {
    case ContentAudience.INTERNAL:
      return 'Internal';
    default:
      return 'External';
  }
}

function getVisibilityLabel(
  publicationState: WorkflowState | undefined,
  draftState: WorkflowState | undefined,
): VisibilityLabel {
  const state = publicationState ?? draftState;
  if (!state) return 'Hidden';

  const visibility = workflowStateToVisibilityMap.get(state);
  if (visibility === 'Internal' || visibility === 'External') {
    return visibility;
  }

  return 'Hidden';
}

// The bar raiser alias is shown only once a bar raiser (not an admin) has
// approved or rejected the content.
function getBarRaiserReviewLabel(workflow: ContentWorkflow | undefined): string | undefined {
  const reviewStatus = workflow?.publisherReviewInfo?.reviewStatus;
  if (!reviewStatus) return undefined;
  if (reviewStatus === PublisherReviewStatus.APPROVED) return 'BR approved';
  if (reviewStatus === PublisherReviewStatus.REJECTED) return 'BR rejected';
  return 'BR approval pending';
}

function getBarRaiserAlias(workflow: ContentWorkflow | undefined): string | undefined {
  const review = workflow?.publisherReviewInfo;
  const decidedByBarRaiser =
    review?.approverType === ApproverType.BAR_RAISER &&
    (review.reviewStatus === PublisherReviewStatus.APPROVED ||
      review.reviewStatus === PublisherReviewStatus.REJECTED);
  if (!decidedByBarRaiser || !workflow?.barRaiserId) return undefined;
  return extractUserAlias(workflow.barRaiserId);
}

/**
 * View model hook for the publication metadata block (Stage, Target audience,
 * Current visibility, and the deciding Bar raiser).
 */
export function usePublicationMetadataViewModel(): PublicationMetadataViewModel {
  const workflow = useWorkflow();

  return {
    stageLabel: getStageLabel(workflow?.draftState),
    audienceLabel: getAudienceLabel(workflow?.contentAudience),
    visibilityLabel: getVisibilityLabel(workflow?.publicationState, workflow?.draftState),
    barRaiserAlias: getBarRaiserAlias(workflow),
    barRaiserReviewLabel: getBarRaiserReviewLabel(workflow),
  };
}
